import { useState } from "react";
import { useAuth } from "../context/AuthContext";
import { useNavigate, Link } from "react-router-dom"; // ✅ Agregado Link
import {
  Eye,
  EyeOff,
  Mail,
  Lock,
  LogIn,
  Loader2,
  AlertCircle,
} from "lucide-react";
import toast from "react-hot-toast";

const Login = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState("");
  const { login, loading } = useAuth();
  const navigate = useNavigate();
  // ✅ LÓGICA DE CONTROL DE ACCESO RÁPIDO
  // Se activará si la variable es "true" en el .env o en Render
  const showQuickAccess = process.env.REACT_APP_ENABLE_QUICK_ACCESS === "true";
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    // Validaciones
    if (!email || !password) {
      setError("Por favor, completa todos los campos");
      toast.error("Por favor, completa todos los campos");
      return;
    }

    // Validar formato de email
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setError("Por favor, ingresa un email válido");
      toast.error("Por favor, ingresa un email válido");
      return;
    }

    setIsSubmitting(true);

    try {
      // Llamar al servicio de login con email y password
      const result = await login(email, password);

      if (result.success) {
        navigate("/");
      } else {
        setError(result.error || "Error al iniciar sesión");
      }
    } catch (error) {
      setError("Error de conexión. Por favor, intenta nuevamente.");
      console.error("Error en login:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Quick access para desarrollo/demo (opcional, puedes eliminar esto en producción)
  const quickAccess = [
    {
      email: "admin@vilox.com",
      role: "Administrador",
      color: "from-purple-500 to-indigo-600",
    },
    {
      email: "transportista@vilox.com",
      role: "Transportista",
      color: "from-blue-500 to-cyan-600",
    },
    {
      email: "operador@vilox.com",
      role: "Operador",
      color: "from-green-500 to-teal-600",
    },
    {
      email: "cliente@vilox.com",
      role: "Cliente",
      color: "from-orange-500 to-red-600",
    },
  ];

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900">
        <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-8 border border-white/20">
          <Loader2 className="h-10 w-10 animate-spin text-white mx-auto" />
          <p className="text-white/80 mt-4 text-center font-medium">
            Verificando sesión...
          </p>
        </div>
      </div>
    );
  }

  const backgroundPattern = `data:image/svg+xml,%3Csvg width="60" height="60" viewBox="0 0 60 60" xmlns="http://www.w3.org/2000/svg"%3E%3Cg fill="none" fill-rule="evenodd"%3E%3Cg fill="%239C92AC" fill-opacity="0.1"%3E%3Ccircle cx="30" cy="30" r="4"/%3E%3C/g%3E%3C/g%3E%3C/svg%3E`;

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 p-4">
      <div
        className="absolute inset-0 opacity-20"
        style={{ backgroundImage: `url("${backgroundPattern}")` }}
      ></div>

      <div className="relative bg-white/95 backdrop-blur-xl p-8 rounded-3xl shadow-2xl w-full max-w-md border border-white/20">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="mx-auto h-20 w-20 bg-gradient-to-r from-purple-500 to-indigo-600 rounded-2xl flex items-center justify-center mb-6 shadow-xl transform transition-all duration-300">
            <LogIn className="h-10 w-10 text-white" />
          </div>
          <h1 className="text-4xl font-bold bg-gradient-to-r from-gray-800 to-gray-600 bg-clip-text text-transparent mb-2">
            Sistema {process.env.REACT_APP_BRAND_NAME || "Synkro Fleet"}
          </h1>
          <p className="text-gray-600 font-medium">
            Sistema de Gestión Logística y Tracking GPS
          </p>
        </div>

        {/* Mensaje de error */}
        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-center">
            <AlertCircle className="h-5 w-5 text-red-500 mr-2" />
            <p className="text-sm text-red-700">{error}</p>
          </div>
        )}

        {/* ✅ QUICK ACCESS: Solo se renderiza si la variable es "true" */}
        {showQuickAccess && (
          <div className="mb-6 p-4 bg-gray-50/50 rounded-2xl border border-gray-100">
            <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-3 text-center">
              Acceso Rápido de Desarrollo
            </p>
            <div className="grid grid-cols-2 gap-2">
              {quickAccess.map((access) => (
                <button
                  key={access.email}
                  type="button"
                  onClick={() => {
                    setEmail(access.email);
                    setPassword("password");
                  }}
                  className={`p-2 rounded-xl text-xs font-bold bg-gradient-to-r ${access.color} text-white hover:shadow-lg transform hover:scale-105 transition-all duration-200 shadow-sm`}
                >
                  {access.role}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Formulario */}
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Campo Email */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Correo Electrónico
            </label>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full pl-10 pr-4 py-3 border-2 border-gray-200 rounded-xl focus:border-purple-500 focus:ring-4 focus:ring-purple-200 transition-all duration-200 outline-none bg-white/70"
                placeholder="correo@ejemplo.com"
                disabled={isSubmitting}
                autoComplete="email"
                autoFocus
              />
            </div>
          </div>

          {/* Campo Contraseña */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Contraseña
            </label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
              <input
                type={showPassword ? "text" : "password"}
                value={password}
                translate="no"
                onChange={(e) => setPassword(e.target.value)}
                className="w-full pl-10 pr-12 py-3 border-2 border-gray-200 rounded-xl focus:border-purple-500 focus:ring-4 focus:ring-purple-200 transition-all duration-200 outline-none bg-white/70"
                placeholder="Ingresa tu contraseña"
                disabled={isSubmitting}
                autoComplete="current-password"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                disabled={isSubmitting}
              >
                {showPassword ? (
                  <EyeOff className="h-5 w-5" />
                ) : (
                  <Eye className="h-5 w-5" />
                )}
              </button>
            </div>
          </div>

          {/* Enlaces adicionales */}
          <div className="flex items-center justify-between text-sm">
            <label className="flex items-center">
              <input type="checkbox" className="mr-2 rounded" />
              <span className="text-gray-600">Recordar sesión</span>
            </label>
            <Link
              to="/forgot-password"
              className="text-purple-600 hover:text-purple-700 font-medium"
            >
              ¿Olvidaste tu contraseña?
            </Link>
          </div>

          {/* Botón Submit */}
          <button
            type="submit"
            disabled={isSubmitting}
            className="w-full bg-gradient-to-r from-purple-500 to-indigo-600 text-white py-4 rounded-xl font-bold shadow-lg hover:shadow-xl transform hover:-translate-y-1 transition-all duration-300 disabled:opacity-70 disabled:cursor-not-allowed disabled:transform-none flex items-center justify-center text-lg hover:scale-105"
          >
            {isSubmitting ? (
              <>
                <Loader2 className="animate-spin h-6 w-6 mr-3" />
                <span>Iniciando sesión...</span>
              </>
            ) : (
              <>
                <LogIn className="h-6 w-6 mr-3" />
                <span>Iniciar Sesión</span>
              </>
            )}
          </button>
        </form>

        {/* Footer */}
        <div className="mt-8 text-center">
          <p className="text-sm text-gray-500">
            Sistema de Gestión Logística • {process.env.REACT_APP_BRAND_NAME || "Synkro Fleet"}
          </p>
          <p className="text-xs text-gray-400 mt-2">
            © 2024 {process.env.REACT_APP_BRAND_NAME || "Synkro Fleet"}. Todos los derechos reservados.
          </p>
          <div className="mt-3 flex justify-center space-x-2">
            <div className="h-2 w-2 rounded-full bg-purple-400"></div>
            <div className="h-2 w-2 rounded-full bg-purple-300"></div>
            <div className="h-2 w-2 rounded-full bg-purple-200"></div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
